import { If } from "components";
import { useSearchParameter } from "hooks";
import { t } from "i18next";
import { OrderModal } from "modules/orders";
import { useMemo, useState } from "react";
import { InfoTooltipIcon, Pagination, SearchInput, Switch } from "ui";

import { ProfileSummaryCards, ResellerReferralsTable } from "../../components";
import { formatAmountInFcfa } from "../../helpers";
import { useResellerReferralsList } from "../../hooks";
import { ResellerReferral } from "../../resellers.types";
import { ReferralOrders } from "./atoms";
import { ResellerReferralsProps } from "./ResellerReferrals.types";

import styles from "./ResellerReferrals.module.scss";

const ResellerReferrals = ({ permanentCode }: ResellerReferralsProps) => {
  const {
    resellerId,
    referrals,
    summary,
    totalItems,
    isLoading,
    page,
    setPage,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    activeOnly,
    toggleActiveOnly
  } = useResellerReferralsList();

  // The drill-down lives in the URL so a referree's orders can be linked to and
  // the browser's back button behaves the way people expect.
  const [openedReferralId, setOpenedReferralId] =
    useSearchParameter("referralId");
  const [selectedReferral, setSelectedReferral] =
    useState<ResellerReferral | null>(null);
  const [openedOrderId, setOpenedOrderId] = useState<string | null>(null);

  const openedReferral = useMemo(() => {
    if (!openedReferralId) return undefined;

    return (
      selectedReferral ??
      referrals?.find((referral) => referral.id === openedReferralId)
    );
  }, [openedReferralId, selectedReferral, referrals]);

  const activeSharePercentage =
    summary?.totalReferrees && summary.totalReferrees > 0
      ? Math.round((summary.activeReferrees / summary.totalReferrees) * 100)
      : 0;

  return (
    <div className={styles.referrals}>
      {openedReferralId ? (
        <ReferralOrders
          resellerId={resellerId}
          referralId={String(openedReferralId)}
          referral={openedReferral}
          onOpenOrder={setOpenedOrderId}
          onGoBack={() => {
            setOpenedReferralId(null);
            setSelectedReferral(null);
          }}
        />
      ) : (
        <>
          {/* "Active referees" ignores the toggle below on purpose — otherwise it
              would always read 100% once the filter is on. */}
          <ProfileSummaryCards
            isLoading={isLoading && !summary}
            cards={[
              {
                label: "Total referees",
                value: summary?.totalReferrees ?? 0,
                hint: t("Linked to this influencer"),
                accent: "primary"
              },
              {
                label: "Active referees",
                value: summary?.activeReferrees ?? 0,
                hint: `${activeSharePercentage}% ${t("placed an order")}`
              },
              {
                label: "Orders generated",
                value: summary?.ordersGenerated ?? 0
              },
              {
                label: "Revenue generated",
                value: formatAmountInFcfa(summary?.revenueGenerated),
                hint: t("Cancelled and failed orders excluded")
              }
            ]}
          />

          <div className={styles.referrals_toolbar}>
            <div className={styles.referrals_search}>
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder={t("Name or phone number")}
              />
            </div>

            <div className={styles.referrals_switch}>
              <Switch
                label={t("With at least one order")}
                checked={activeOnly}
                onChange={toggleActiveOnly}
              />
            </div>
          </div>

          <div className={styles.referrals_table}>
            <ResellerReferralsTable
              referrals={referrals ?? []}
              isLoading={isLoading}
              sorting={sorting}
              setSorting={setSorting}
              fallbackCode={permanentCode}
              onRowClick={(referral) => {
                setSelectedReferral(referral);
                setOpenedReferralId(referral.id);
              }}
            />
          </div>

          <div className={styles.referrals_note}>
            <InfoTooltipIcon
              id="referrals-scope-tooltip"
              tooltipText={t(
                "The order count includes cancelled orders; revenue and commission do not. Commission only counts orders where this influencer was the earner."
              )}
            />
            {t("How are these numbers counted?")}
          </div>

          <Pagination
            currentPage={page}
            totalItems={totalItems}
            onChange={setPage}
          />
        </>
      )}

      <If condition={!!openedOrderId}>
        <OrderModal
          orderId={String(openedOrderId)}
          isOpen={!!openedOrderId}
          refetchOrders={() => {}}
          onClose={() => setOpenedOrderId(null)}
        />
      </If>
    </div>
  );
};

export default ResellerReferrals;
